import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockHead,
  Icon,
} from "../../components/Component";
import { Badge, Button } from "reactstrap";
import UpdateForumModal from "./modals/UpdateForumModal";
import DeleteConfirmationModal from "../admin/modals/DeleteConfimationModal";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  deleteForumSuccess,
  getListForum,
  reorderForumSuccess,
} from "../../slice/forums";
import { deleteForum, reorderForum } from "../../api/forum";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import TableActions from "../../components/table/TableActions";
import TableText from "../../components/table/TableText";
// Forum name / desc / image are now `{ en, ar? }` LocalizedText objects.
// Legacy rows may still be plain strings until the BE migration runs.
const readEn = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.en ?? v.ar ?? "";
};
const readAr = (v) => {
  if (!v || typeof v === "string") return "";
  return v.ar ?? "";
};

function getDefaultColumns(onEditClick, onDeleteClick) {
  return [
    {
      head: <span className="sub-text">{"Image"}</span>,
      cell: (row) =>
        readEn(row.image) ? (
          <img src={readEn(row.image)} alt="" className="app-table-thumb" />
        ) : (
          <span className="app-table-thumb"></span>
        ),
    },
    {
      head: <span className="sub-text">{"Name"}</span>,
      cell: (row) => (
        <TableText
          text={readEn(row.name)}
          sub={readAr(row.name)}
          maxWidth={220}
          lines={1}
        />
      ),
    },
    {
      head: <span className="sub-text">{"Description"}</span>,
      cell: (row) => (
        <TableText
          text={readEn(row.desc)}
          sub={readAr(row.desc)}
          maxWidth={320}
        />
      ),
    },
    {
      head: <span className="sub-text">{"Members"}</span>,
      cell: (row) => <span>{(row.members ?? []).length}</span>,
    },
    {
      head: <span className="sub-text">{"Favorites"}</span>,
      cell: (row) => <span>{row.totalLikes ?? 0}</span>,
    },
    {
      head: <span className="sub-text">{"Status"}</span>,
      cell: (row) => (
        <span>
          <Badge
            className="badge-sm badge-dot has-bg d-none d-sm-inline-flex"
            color={row.active ? "success" : "warning"}
          >
            {row.active ? "Active" : "Deactive"}
          </Badge>
        </span>
      ),
    },
    {
      head: <span className="sub-text"></span>,
      cellClassName: "nk-tb-col-tools",
      cell: (row) => (
        <TableActions
          actions={[
            { label: "Edit", icon: "eye", onClick: () => onEditClick(row) },
            {
              label: "Delete",
              icon: "delete",
              danger: true,
              onClick: () => onDeleteClick(row),
            },
          ]}
        />
      ),
    },
  ];
}
const ForumsPage = () => {
  const [smOption, setSmOption] = useState(true);
  const { list, summary, loading } = useSelector((state) => state.forums);
  const [params, setParams] = useState({ page: 1, limit: 50 });
  const dispatch = useDispatch();

  const [isForumModalVisible, setForumModalVisible] = useState({
    forum: null,
    update: false,
    delete: false,
  });
  const openModal = (forum) => {
    setForumModalVisible({ forum, update: true });
  };
  const closeModal = () => {
    setForumModalVisible({
      forum: null,
      delete: false,
      update: false,
    });
  };
  const onDelete = (forum) => {
    setForumModalVisible({ forum, delete: true });
  };
  const dataTableColumns = getDefaultColumns(openModal, onDelete);
  useEffect(() => {
    dispatch(getListForum(params));
  }, [dispatch, params]);
  const handlePageChange = (page) => {
    setParams({
      ...params,
      page,
    });
  };
  /// func
  const onDeleteForum = async () => {
    try {
      await deleteForum(isForumModalVisible.forum?._id);
      dispatch(deleteForumSuccess(isForumModalVisible.forum));
      toast.success("Delete forum successfully");
    } catch (error) {
      toast.error(error);
    }
  };

  const onReorderForum = async (payload) => {
    try {
      await reorderForum(payload);
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <React.Fragment>
      <Head title="Forums"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Forums</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand me-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="nk-block-tools g-3">
                    <li className="nk-block-tools-opt">
                      <Button
                        className="toggle btn-icon d-md-none"
                        color="primary"
                        onClick={() => {
                          openModal();
                        }}
                      >
                        <Icon name="plus"></Icon>
                      </Button>
                      <Button
                        className="toggle d-none d-md-inline-flex"
                        color="primary"
                        onClick={() => {
                          openModal();
                        }}
                      >
                        <Icon name="plus"></Icon>
                        <span>Add Forum</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <BasePaginationTable
          list={list}
          loading={loading}
          columns={dataTableColumns}
          onPageChange={handlePageChange}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          onReorder={({ item, to }) => {
            const data = { id: item._id, nextId: list[to]._id };
            dispatch(reorderForumSuccess(data));
            onReorderForum(data);
          }}
          {...params}
        />
      </Content>
      <UpdateForumModal
        forum={isForumModalVisible.forum}
        isOpen={isForumModalVisible.update}
        closeModal={closeModal}
      />
      <DeleteConfirmationModal
        onConfirm={onDeleteForum}
        isOpen={isForumModalVisible.delete}
        onCloseModal={closeModal}
      />
    </React.Fragment>
  );
};

export default ForumsPage;
